import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { useNavigate } from "react-router-dom";
import { Search, Filter, MapPin, Target, User, Plus, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTalentMarketplace } from "@/hooks/useTalentMarketplace";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useShortlists } from "@/hooks/useShortlists";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSkillById } from "@/lib/skills";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function EmpresaVitrine() {
  const navigate = useNavigate();
  const { orgId } = usePartnerContext();
  const { talents, isLoading, logAudit } = useTalentMarketplace(orgId);
  const { shortlists, addItem, createShortlist } = useShortlists(orgId);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newShortlistName, setNewShortlistName] = useState("");
  const [pendingTalentId, setPendingTalentId] = useState<string | null>(null);

  const talentIds = talents.map(t => t.id);
  const { data: allTalentSkills } = useQuery({
    queryKey: ["talent_skills", talentIds],
    enabled: talentIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_skills" as any)
        .select("user_id, skill_id")
        .in("user_id", talentIds);
      if (error) throw error;
      return data as any[];
    },
  });

  const getSkillsForTalent = (userId: string) => {
    return (allTalentSkills ?? []).filter((s: any) => s.user_id === userId).map((s: any) => s.skill_id as string);
  };

  const filteredTalentos = talents.filter((t) =>
    (t.full_name ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.objective ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewProfile = (talentoId: string) => {
    logAudit.mutate({ action: "view_profile", entityType: "profile", entityId: talentoId });
    navigate(`/empresa/talento/${talentoId}`);
  };

  const handleAddToShortlist = (shortlistId: string, shortlistName: string, talentId: string) => {
    addItem.mutate({ shortlistId, userId: talentId }, {
      onSuccess: () => toast.success(`Adicionado à shortlist "${shortlistName}"`),
      onError: () => toast.error("Talento já está nesta shortlist ou erro ao adicionar"),
    });
  };

  const handleCreateAndAdd = async () => {
    if (!newShortlistName.trim() || !pendingTalentId) return;
    try {
      const newSl = await createShortlist.mutateAsync(newShortlistName.trim());
      addItem.mutate({ shortlistId: newSl.id, userId: pendingTalentId }, {
        onSuccess: () => toast.success(`Shortlist "${newShortlistName}" criada e talento adicionado!`),
      });
      setShowCreateDialog(false);
      setNewShortlistName("");
      setPendingTalentId(null);
    } catch {
      toast.error("Erro ao criar shortlist");
    }
  };

  if (isLoading) {
    return <div className="page-container-desktop flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="page-container-desktop">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Vitrine de Talentos</h1>
        <p className="text-muted-foreground mt-1">Encontre jovens talentos prontos para oportunidades</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input placeholder="Buscar por nome ou objetivo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Button variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters(!showFilters)}>
          <Filter className="w-4 h-4" />Filtros
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <p className="text-sm text-muted-foreground mb-4">{filteredTalentos.length} talentos encontrados</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTalentos.map((talento) => {
            const talentSkills = getSkillsForTalent(talento.id).slice(0, 4);
            return (
              <div key={talento.id} onClick={() => handleViewProfile(talento.id)} className="card-interactive cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-foreground">{talento.full_name ?? "Sem nome"}</h3>
                      <StatusBadge status={(talento.profile_status ?? "construction") as any} />
                    </div>
                    {(talento.city || talento.state) && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                        <MapPin className="w-4 h-4" />{[talento.city, talento.state].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {talento.objective && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                        <Target className="w-4 h-4" />{talento.objective}
                      </div>
                    )}
                    {talentSkills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {talentSkills.map(sid => (
                          <Badge key={sid} variant="secondary" className="text-[10px]">{getSkillById(sid)?.label ?? sid}</Badge>
                        ))}
                        {getSkillsForTalent(talento.id).length > 4 && (
                          <Badge variant="outline" className="text-[10px]">+{getSkillsForTalent(talento.id).length - 4}</Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="flex-1" onClick={(e) => e.stopPropagation()}>
                        <Plus className="w-4 h-4" />Shortlist
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56" onClick={(e) => e.stopPropagation()}>
                      {shortlists.length === 0 ? (
                        <DropdownMenuItem disabled className="text-muted-foreground text-xs">Nenhuma shortlist criada</DropdownMenuItem>
                      ) : (
                        shortlists.map((sl: any) => (
                          <DropdownMenuItem key={sl.id} onClick={() => handleAddToShortlist(sl.id, sl.name, talento.id)}>
                            {sl.name}
                          </DropdownMenuItem>
                        ))
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setPendingTalentId(talento.id); setShowCreateDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />Criar nova shortlist
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="default" size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); handleViewProfile(talento.id); }}>
                    Ver Perfil
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {filteredTalentos.length === 0 && (
        <div className="bg-muted/50 rounded-2xl p-12 text-center mt-6">
          <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhum talento encontrado</h3>
          <p className="text-sm text-muted-foreground">Aguarde mais alunos completarem seus perfis</p>
        </div>
      )}

      {/* Create shortlist dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar nova shortlist</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Nome da shortlist"
            value={newShortlistName}
            onChange={(e) => setNewShortlistName(e.target.value)}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateAndAdd} disabled={!newShortlistName.trim()}>Criar e Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
